import PropTypes from 'prop-types';
import cn from 'class-names';
import { useEffect, useRef, useState } from 'react';

// hooks
import useWindowSize from '@/utils/hooks/useWindowSize';
// helpers
import { getContent, isWHEnv } from '@/utils/helper';
import { analytics, getAnchorNavigationDetail } from '@/utils/analytics';
// ui
import CtfCta from '@/molecules/CTA/index';

const MAXIMUM_POSITION_TOP_ACTIVE_PX = 200;

export default function AnchorNavigation({
  anchor: { sys: { id: anchorId } } = {},
  entries,
}) {
  // State
  const movingAnchorRef = useRef();
  const windowSize = useWindowSize();
  const [showFixedAnchor, setShowFixedAnchor] = useState(false);
  // Function
  const handleAnchorNavigate = (e) => {
    analytics({
      window,
      document,
      detail: getAnchorNavigationDetail(e),
    });
  };
  // Var
  const anchorContent = getContent(anchorId, entries);
  const fields = anchorContent?.fields;
  // Effects
  // ===============================
  // ||  listen to scroll events  ||
  // ===============================
  // 1. update anchor navigation status to active when passing element with ID
  useEffect(() => {
    if (!fields?.links?.length) {
      return;
    }

    const handleScrollActivateLink = () => {
      // add/remove anchor class on scroll
      const anchorNavs = document.querySelectorAll(
        '.aaco-anchor-navigation > .aaco-anchor-nav-wrap > div > a'
      );
      const anchorNavsSize = anchorNavs?.length || 0;
      anchorNavs?.forEach((anchor, index) => {
        // 1. get CURRENT anchor position
        const id = anchor?.getAttribute('href');
        const targetElement = document.querySelector(id);
        if (!targetElement) {
          return;
        }
        const targetElementPositon = targetElement?.getBoundingClientRect();

        // 2. get NEXT anchor position
        const nextIndex = index + 1;
        const hasNextIndex = nextIndex < anchorNavsSize;
        let nextTargetElementPositon;
        if (hasNextIndex) {
          const nextAnchor = anchorNavs[nextIndex];
          const nextId = nextAnchor?.getAttribute('href');
          const nextTargetElement = document.querySelector(nextId);

          if (nextTargetElement) {
            nextTargetElementPositon =
              nextTargetElement?.getBoundingClientRect();
          }
        }

        // 3. check if
        ////// - current anchor top is in the active range
        const isInActiveRange =
          targetElementPositon.top < MAXIMUM_POSITION_TOP_ACTIVE_PX;
        ////// - next anchor top is outside the active range
        const isNextAnchorOutsideActiveRange =
          nextTargetElementPositon?.top > MAXIMUM_POSITION_TOP_ACTIVE_PX;
        ////// purpose: only mark the last active anchor as active
        const isLastAnchorInActiveRange =
          isInActiveRange && (!hasNextIndex || isNextAnchorOutsideActiveRange);
        if (isLastAnchorInActiveRange) {
          anchor.classList.add('active');
        } else {
          anchor.classList.remove('active');
        }
      });
    };
    window.addEventListener('scroll', handleScrollActivateLink);
    return () =>
      document.removeEventListener('scroll', handleScrollActivateLink);
  }, [fields?.links]);

  // 2. Anchor Navigation position
  //    - show the moving anchor navigation first
  //    - after moving anchor navigation top = 0; show the fix anchor navigation
  useEffect(() => {
    const handleScrollChangePosition = () => {
      const movingAnchor = movingAnchorRef?.current;
      if (isNaN(+window?.pageYOffset) || isNaN(+window?.pageYOffset)) {
        return;
      }

      const movingAnchorPosYExceed =
        +window.pageYOffset > +movingAnchor?.offsetTop;
      if (!showFixedAnchor && movingAnchorPosYExceed) {
        setShowFixedAnchor(true);
      } else if (!movingAnchorPosYExceed) setShowFixedAnchor(false);
    };

    if (movingAnchorRef?.current) {
      window.addEventListener('scroll', handleScrollChangePosition);
    }
    return () => {
      document.removeEventListener('scroll', handleScrollChangePosition);
    };
  }, [movingAnchorRef, windowSize?.windowHeight]);

  return (
    <div
      className={cn(
        'aaco-anchor-navigation__container border-1 border-y-black bg-warmer-grey aaco-anchor-navigation--scroll hidden-print'
      )}
    >
      {/* Use 2 anchor navigation & show hide them on scroll */}
      {/* START: moving anchor navigation */}
      <div
        className={cn(
          'aaco-anchor-navigation aaco-anchor-navigation--moving',

          {
            'aaco-anchor-navigation--hidden': showFixedAnchor,
            'container mx-auto my-0': isWHEnv(),
          }
        )}
        ref={movingAnchorRef}
      >
        {fields?.links?.length > 0 &&
          fields.links.map((field, idx) => (
            <div key={field?.sys?.id} id="">
              <CtfCta
                entries={entries}
                field={field}
                noWrap
                hideIcon={true}
                onClick={handleAnchorNavigate}
                className={cn("pr-4 text-body text-black border-r-1 border-black", {
                  "pl-0": idx === 0,
                  "pl-4": idx !== 0,
                })}
              />
            </div>
          ))}
      </div>
      {/* END: moving anchor navigation */}
      {/* START: fix anchor navigation */}
      <div
        className={cn('aaco-anchor-navigation aaco-anchor-navigation--fixed', {
          'aaco-anchor-navigation--hidden': !showFixedAnchor,
          'border-1 border-y-black bg-warmer-grey': isWHEnv()
        })}
      >
        <div className="aaco-anchor-nav-wrap container mx-auto my-0 flex">
          {fields?.links?.length > 0 &&
            fields.links.map((field, idx) => (
              <div key={field?.sys?.id} id="">
                <CtfCta
                  entries={entries}
                  field={field}
                  noWrap
                  hideIcon={true}
                  onClick={handleAnchorNavigate}
                  className={cn("pr-4 text-body text-black border-r-1 border-black", {
                    "pl-0": idx === 0,
                    "pl-4": idx !== 0,
                  })}
                />
              </div>
            ))
          }
        </div>
      </div>
      {/* END: fix anchor navigation */}
    </div>
  );
}

AnchorNavigation.propTypes = {
  anchor: PropTypes.object,
  entries: PropTypes.array,
};
